import React from 'react';
import {
    useGetEnerginetReportmappingsQuery,
    useGetKunderQuery,
} from '../api/rtkApi';
import type { ReportWithMappingFlattened } from '../types';
import { useGetByggListQuery } from '@klp/contextSelector/services/rtkApi';

// Extended type that includes the computed properties used in the flattened list
export type MappingTableData = Omit<
    ReportWithMappingFlattened,
    'byggNummer'
> & {
    byggNummer?: Array<string>;
    byggNavn?: Array<string | null | undefined>;
    kundeNavn?: string;
    isMapped: boolean;
};

type MappingFilter = 'all' | 'mapped' | 'unmapped';

type UseMappingTableDataReturn = {
    filter: MappingFilter;
    setFilter: (filter: MappingFilter) => void;
    flatList: Array<MappingTableData>;
    filteredData: Array<ReportWithMappingFlattened>;
    isLoading: boolean;
    isFetching: boolean;
};

export const useMappingTableData = (): UseMappingTableDataReturn => {
    const [filter, setFilter] = React.useState<MappingFilter>('all');

    const {
        data = [],
        isLoading: isMappingLoading,
        isFetching,
    } = useGetEnerginetReportmappingsQuery();
    const { data: byggList = [], isLoading: byggLoading } = useGetByggListQuery(
        { includeAll: true },
    );
    const { data: kundeList = [], isLoading: kundeLoading } = useGetKunderQuery(
        { navnOrNummer: '' },
    );

    const filteredData = React.useMemo(() => {
        switch (filter) {
            case 'mapped':
                return data.filter((item) => item.mapping);
            case 'unmapped':
                return data.filter((item) => !item.mapping);
            case 'all':
            default:
                return data;
        }
    }, [data, filter]);

    const flatList: Array<MappingTableData> = React.useMemo(
        () =>
            filteredData.map((item) => {
                const byggNummerSplit =
                    item.mapping?.byggNummer.split(',') ?? [];
                const byggNavn = byggList
                    .filter((bygg) =>
                        byggNummerSplit.includes(bygg.nummer ?? ''),
                    )
                    .map((bygg) => bygg.navn);
                return {
                    id: item.id,
                    name: item.name,
                    type: item.type,
                    title: item.title,
                    description: item.description,
                    byggNummer: byggNummerSplit,
                    byggNavn: byggNavn,
                    kundenNummer: item.mapping?.kundenummer,
                    kundeNavn: item.mapping?.kundenummer
                        ? `${item.mapping.kundenummer} - ${
                              kundeList.find(
                                  (k) => k.nummer === item.mapping?.kundenummer,
                              )?.navn ?? ''
                          }`
                        : '',
                    isMapped: !!item.isMapped,
                    mapping: item.mapping,
                };
            }),
        [filteredData, byggList, kundeList],
    );

    const isLoading = isMappingLoading || byggLoading || kundeLoading;

    return {
        filter,
        setFilter,
        flatList,
        filteredData,
        isLoading,
        isFetching,
    };
};
